import * as React from 'react';
import moment from 'moment-timezone';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { useTimer, useTime } from 'react-timer-hook';
import { useRouter } from 'next/router';
import { CircularProgress, Box } from '@mui/material';

import config from 'config';
import { GetSettingsResponse } from 'models/settings';
import { useUrl } from 'hooks/useUrl';
import { ReduxState } from 'ducks';
import { Product, ProductETicketStub } from 'models/product';
import { GuidancePage } from 'models/guidancePage';
import { Reservation } from 'models/reservation';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { CurrentPositionContext } from 'contexts/CurrentPositionContext';
import {
  getCheckinStatus,
  isReservationCanceled,
  isReservationNotConfirmed,
} from 'lib/util/getCheckinStatus';
import { isFreePassReservation } from 'lib/util/isFreePassReservation';
import { leaveFootprint } from 'ducks/client/footprints';
import { getCheckinExpirationLimit } from 'lib/util/getCheckinExpirationLimit';
import { ETicketRedeemTargetContext, GuestTypeCount } from 'contexts/ETicketRedeemTargetContext';
import {
  getCheckinStubDisplayText,
  getCheckinTimeText,
  isCheckinedStub,
  isCancellableSetting,
} from 'lib/util/eTicket';
import { ETicketRedemptionCard } from 'components/ETicketRedemptionCard/ETicketRedemptionCard';
import { ETicketGuestCountSelector } from 'components/ETicketGuestCountSelector/ETicketGuestCountSelector';
import { ETicketShareUrlButtons } from 'components/ETicketShareUrlButtons/ETicketShareUrlButtons';
import { SpecialOfferPopup } from 'components/SpecialOfferPopup/SpecialOfferPopup';
import { formatTextWithLineBreaks } from 'lib/util/formatTextWithLineBreaks';

import { KeyVisual } from './KeyVisual';
import styles from './ETicketPage.module.css';
import { selectApplicableSalesOffers } from 'ducks/client/salesOffers';

export const ETicketCard = ({
  reservation,
  product,
  onFootprintClick,
  showFootprintedModal,
  guidancePages,
  initialOpen,
  host,
}: {
  reservation: Reservation;
  product: Product;
  onFootprintClick: () => void;
  showFootprintedModal?: boolean;
  guidancePages?: GuidancePage[] | null;
  initialOpen?: boolean;
  host: string;
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { apiKey } = React.useContext(ApiKeyContext);
  const { currentPosition } = React.useContext(CurrentPositionContext);
  const settings = useSelector((state: ReduxState) => state.server.settings.all);

  const [showSpecialOffer, setShowSpecialOffer] = React.useState<boolean>(false);
  const [showDetails, setShowDetails] = React.useState<boolean>(initialOpen ?? true);
  const [showTimer, setShowTimer] = React.useState<boolean>(false);

  const guidanceUrl = useUrl(`/guidance/list?r=${reservation.id}`);

  const notConfirm = isReservationNotConfirmed(reservation);
  const checkinStatus = getCheckinStatus(reservation, product);
  const salesOffers = useSelector(selectApplicableSalesOffers);

  React.useEffect(() => {
    setShowTimer(true);
  }, []);
  React.useEffect(() => {
    if (salesOffers?.length) {
      const timeoutId = setTimeout(() => {
        setShowSpecialOffer(true);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [salesOffers]);

  return (
    <>
      <section className={clsx(styles['main__list__item'])}>
        <div className={clsx(styles['card'])}>
          <div className={clsx(styles['card__pic'])}>
            <KeyVisual reservation={reservation} product={product} />
          </div>

          <div className={clsx(styles['card__content'])}>
            <div className={clsx(styles['card__content__pic'])}></div>

            <h2 className={clsx(styles['card__ttl'])}>{reservation.product_name}</h2>

            <Guest reservation={reservation} />

            <>
              {config.enableSeatManagement && checkinStatus === 'started' && (
                <EquipmentBlockReference reservation={reservation} />
              )}
            </>

            <>
              {checkinStatus !== 'expired' && showTimer && (
                <ExpirationDate reservation={reservation} product={product} />
              )}
            </>

            <>
              {checkinStatus === 'expired' && (
                <RedemptionDate reservation={reservation} product={product} />
              )}
            </>

            <>
              {checkinStatus !== 'expired' && showDetails && (
                <>
                  {getCheckinStubs(product).length > 0 ? (
                    <ActionButtonsForMultipleCheckin
                      reservation={reservation}
                      product={product}
                      settings={settings}
                    />
                  ) : (
                    <ActionButtons
                      reservation={reservation}
                      product={product}
                      settings={settings}
                    />
                  )}
                </>
              )}
            </>

            {config.enableCrossSellUpSell && (salesOffers?.length ?? 0) > 0 && (
              <div className={clsx(styles['card__acContent'], styles['is-active'])}>
                <ul className={clsx(styles['card__action'])}>
                  <li className={clsx(styles['card__action__item'])}>
                    <button
                      className={clsx(styles['btn'], styles['btn--submit'])}
                      onClick={() => {
                        setShowSpecialOffer(true);
                      }}
                    >
                      {t('Special Offer')}
                    </button>
                  </li>
                </ul>
              </div>
            )}

            <>{checkinStatus === 'expired' && <BuyAgainButton reservation={reservation} />}</>

            {!notConfirm && (
              <>
                {getCheckinStatus(reservation, product) === 'started' &&
                  (isFreePassReservation(reservation) ||
                    product?.qr_checkin_settings?.should_use_foot_print) && (
                    <div className={clsx(styles['card__show'])}>
                      <a
                        className={clsx(styles['btn'], styles['btn--double'])}
                        onClick={async () => {
                          if (!showFootprintedModal) {
                            try {
                              await dispatch(
                                leaveFootprint(apiKey, {
                                  reservation_id: reservation.id,
                                  latitude: currentPosition?.lat() ?? 0,
                                  longitude: currentPosition?.lng() ?? 0,
                                })
                              );
                            } catch (e) {
                              console.error(e);
                            }
                            onFootprintClick();
                          }
                        }}
                      >
                        {t('Show')}
                      </a>
                    </div>
                  )}
              </>
            )}
            <>
              {checkinStatus === 'started' && (guidancePages ?? []).length > 0 && (
                <div className={styles['card__coupon']}>
                  <a
                    className={clsx(styles['btnSmall'], styles['btn--submit'])}
                    href={guidanceUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      className={styles['btnSmall__coupon']}
                      src={
                        product?.qr_checkin_settings?.guidance_button_image_url ||
                        '/static/images/ic_coupon.svg'
                      }
                    />
                    {product?.qr_checkin_settings?.guidance_button_text?.replace(/\u3000/g, ' ') ||
                      t('Coupon')}
                  </a>
                </div>
              )}
            </>

            <>
              {config.enableSplitCheckin &&
                product?.qr_checkin_settings?.should_count_guests_for_checkin_with_guest_type && (
                  <ETicketShareUrlButtons reservation={reservation} host={host} />
                )}
            </>

            <>
              {product?.qr_checkin_settings?.terms_of_service_url && (
                <a
                  className={clsx(styles['card__terms'])}
                  href={product?.qr_checkin_settings?.terms_of_service_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('Terms of Service')}
                  <img src="/static/images/ic_newwindow_blue.svg" />
                </a>
              )}
            </>

            {checkinStatus === 'not_started' && (
              <a
                className={clsx(
                  styles['btn'],
                  styles['btn--ac'],
                  !showDetails ? styles['is-open'] : styles['is-close']
                )}
                onClick={() => {
                  setShowDetails(!showDetails);
                }}
              >
                {showDetails ? <>{t('Close')}</> : <>{t('See details')}</>}
              </a>
            )}
          </div>
        </div>
      </section>
      {showSpecialOffer && salesOffers && salesOffers.length > 0 && (
        <SpecialOfferPopup
          reservation={reservation}
          onClose={() => setShowSpecialOffer(false)}
          offer={salesOffers[0]}
        />
      )}
    </>
  );
};

// show buy again button
const BuyAgainButton = ({ reservation }: { reservation: Reservation }) => {
  const { t } = useTranslation();
  const url = useUrl(`/products/${reservation.product_id}`);

  return (
    <div className={clsx(styles['card__acContent'], styles['is-active'])}>
      <ul className={clsx(styles['card__action'])}>
        <li className={clsx(styles['card__action__item'])}>
          <Link href={url} className={clsx(styles['btn'], styles['btn--submit'])}>
            {t('Purchase again')}
          </Link>
        </li>
      </ul>
    </div>
  );
};

// Show guests area
const Guest = ({ reservation }: { reservation: Reservation }) => {
  const { t } = useTranslation();
  const remainingGuestTypeKeys: string[] = [];
  const remainingGuestTypeText: { [index: string]: string } = {};
  reservation.guests.forEach((guest) => {
    if (!['大人', 'Adult', '子供', 'Child'].includes(guest.guest_type_key)) {
      if (!remainingGuestTypeKeys.includes(guest.guest_type_key)) {
        remainingGuestTypeKeys.push(guest.guest_type_key);
        remainingGuestTypeText[guest.guest_type_key] = guest.guest_type_title ?? '';
      }
    }
  });

  const numOfAdults = reservation.guests.filter((guest) =>
    ['大人', 'Adult'].includes(guest.guest_type_key)
  ).length;
  const numOfChildren = reservation.guests.filter((guest) =>
    ['子供', 'Child'].includes(guest.guest_type_key)
  ).length;

  return (
    <ul className={clsx(styles['card__member'])}>
      {numOfAdults > 0 && (
        <li className={clsx(styles['card__member__item'], numOfChildren ? null : styles['full'])}>
          <p className={clsx(styles['card__member__item__ttl'])}>{t('Adult')}</p>
          <p className={clsx(styles['card__member__item__num'])}>{numOfAdults}</p>
        </li>
      )}
      {numOfChildren > 0 && (
        <li className={clsx(styles['card__member__item'], numOfAdults ? null : styles['full'])}>
          <p className={clsx(styles['card__member__item__ttl'])}>{t('Child')}</p>
          <p className={clsx(styles['card__member__item__num'])}>{numOfChildren}</p>
        </li>
      )}

      {remainingGuestTypeKeys.map((guestTypeKey, idx) => (
        <li className={clsx(styles['card__member__item'], styles['full'])} key={idx}>
          <p className={clsx(styles['card__member__item__ttl'])}>
            {`${remainingGuestTypeText[guestTypeKey]} : ${getGuestCountByKey(
              reservation,
              guestTypeKey
            )}`}
          </p>
        </li>
      ))}
    </ul>
  );
};

// Show expiration date
const EquipmentBlockReference = ({ reservation }: { reservation: Reservation }) => {
  const { t } = useTranslation();

  return (
    <>
      {(reservation?.equipment_block_references ?? []).length > 0 && (
        <div className={clsx(styles['card__equipment-block-reference'])}>
          <p className={clsx(styles['card__equipment-block-reference__ttl'])}>{t('Seat')}</p>
          <div className={clsx(styles['card__equipment-block-reference__txt'])}>
            <p>
              <span>{reservation.equipment_block_references.join('/')}</span>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

// Show expiration date
const ExpirationDate = ({
  reservation,
  product,
}: {
  reservation: Reservation;
  product: Product;
}) => {
  const { t, i18n } = useTranslation();

  return (
    <>
      {reservation.free_start_date_time_from ? (
        <div className={clsx(styles['card__limit'])}>
          <p className={clsx(styles['card__limit__ttl'])}>{t('Expiry date')}</p>
          <div className={clsx(styles['card__limit__txt'])}>
            {getCheckinStatus(reservation, product) === 'started' &&
            product?.qr_checkin_settings?.expiration_date?.relative_date?.type !== 'ETERNAL' ? (
              <p>
                {i18n.language === 'ja-JP' ? (
                  <>
                    <span>{moment().format('MM月DD日')}</span>
                    に限り有効
                  </>
                ) : (
                  <>
                    Valid only on <span>{moment().format('MM/DD')}</span>
                  </>
                )}
                <span className={clsx(styles['card__limit__txt__timer'])}>
                  <Timer reservation={reservation} product={product} />
                </span>
              </p>
            ) : (
              <p>
                {i18n.language === 'ja-JP' ? (
                  <>
                    <span>
                      {moment
                        .tz(reservation?.start_date_time_utc, reservation?.start_timezone)
                        .format('YYYY年MM月DD日')}
                    </span>
                    まで有効
                  </>
                ) : (
                  <>
                    Valid until{' '}
                    <span>
                      {moment
                        .tz(reservation?.start_date_time_utc, reservation?.start_timezone)
                        .format('YYYY/MM/DD')}
                    </span>
                  </>
                )}
              </p>
            )}
          </div>
        </div>
      ) : (
        <>
          {getCheckinStatus(reservation, product) === 'started' ? (
            <div className={clsx(styles['card__limit'])}>
              <p className={clsx(styles['card__limit__ttl'])}>
                {t('Participation')} <StartDateTime reservation={reservation} />{' '}
              </p>
              <div className={clsx(styles['card__limit__txt'])}>
                <p>
                  <Timer reservation={reservation} product={product} />
                </p>
              </div>
            </div>
          ) : (
            <div className={clsx(styles['card__limit'])}>
              <p className={clsx(styles['card__limit__ttl'])}>{t('Participation')} </p>
              <div className={clsx(styles['card__limit__txt'])}>
                <p>
                  <span>
                    <StartDateTime reservation={reservation} />
                  </span>
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

const StartDateTime = ({ reservation }: { reservation: Reservation }) => {
  const { i18n } = useTranslation();
  const startDateTimeLocal = moment.tz(
    reservation.start_date_time_utc,
    reservation.start_timezone || 'UTC'
  );

  return (
    <>
      {`${
        i18n.language === 'ja-JP'
          ? startDateTimeLocal.format('YYYY年MM月DD日')
          : startDateTimeLocal.format('YYYY/MM/DD')
      }`}{' '}
      <br />{' '}
      {`${
        i18n.language === 'ja-JP'
          ? startDateTimeLocal.format('HH:mm')
          : startDateTimeLocal.format('hh:mm') + (startDateTimeLocal.hours() >= 12 ? 'PM' : 'AM')
      } ${reservation.start_time_description ?? ''}`}
    </>
  );
};

const Timer = ({ reservation, product }: { reservation: Reservation; product: Product }) => {
  if (product.qr_checkin_settings?.expiration_date?.display_type === 'CLOCK') {
    return <Clock />;
  }

  return <CountDownTimer reservation={reservation} product={product} />;
};

const CountDownTimer = ({
  reservation,
  product,
}: {
  reservation: Reservation;
  product: Product;
}) => {
  const { t } = useTranslation();
  const limit = getCheckinExpirationLimit(reservation, product);

  const { days, seconds, minutes, hours, isRunning } = useTimer({
    expiryTimestamp: limit?.toDate() ?? new Date(),
  });

  if (!limit) {
    return <></>;
  }

  if (isRunning) {
    return (
      <p>
        {days > 0 && t('{{days}} days', { days })}{' '}
        {`${fillZero(hours)}:${fillZero(minutes)}:${fillZero(seconds)}`}
      </p>
    );
  }
  return <></>;
};

const Clock = () => {
  const { i18n } = useTranslation();
  const { seconds, minutes, hours, ampm } = useTime({ format: '12-hour' });
  return (
    <>
      {i18n.language === 'ja-JP' && <>{`${ampm === 'pm' ? '午後' : '午前'}`}</>}{' '}
      {`${('00' + hours).slice(-2)}:${('00' + minutes).slice(-2)}:${('00' + seconds).slice(-2)}`}{' '}
      {i18n.language !== 'ja-JP' && <>{`${ampm === 'pm' ? 'PM' : 'AM'}`}</>}
    </>
  );
};

// Show action buttons for multiple checkin reservation
const ActionButtonsForMultipleCheckin = ({
  reservation,
  product,
  settings,
}: {
  reservation: Reservation;
  product: Product;
  settings: GetSettingsResponse;
}) => {
  const canceled = isReservationCanceled(reservation);

  return (
    <div className={clsx(styles['card__acContent'], styles['is-active'])}>
      <ul className={clsx(styles['card__action'])}>
        {config.enableSplitCheckin ? (
          <>
            {getCheckinStubs(product).map((stub) => {
              return (
                <ETicketRedemptionCard
                  reservation={reservation}
                  product={product}
                  stub={stub}
                  key={stub.key}
                />
              );
            })}
          </>
        ) : (
          <>
            {getCheckinStubs(product).map((stub) => {
              return (
                <MultipleCheckinLineItem
                  reservation={reservation}
                  product={product}
                  stub={stub}
                  key={stub.key}
                />
              );
            })}
          </>
        )}

        {canceled ? (
          <li className={clsx(styles['card__action__item'])}>
            <ReservationCanceledButton />
          </li>
        ) : (
          <>
            {isCancellableSetting(product, settings, reservation) && (
              <li className={clsx(styles['card__action__item'])}>
                <CancelButton reservation={reservation} />
              </li>
            )}
          </>
        )}
      </ul>
    </div>
  );
};

const MultipleCheckinLineItem = ({
  reservation,
  product,
  stub,
}: {
  reservation: Reservation;
  product: Product;
  stub: ProductETicketStub;
}) => {
  const { i18n } = useTranslation();
  const notConfirm = isReservationNotConfirmed(reservation);
  return (
    <li className={clsx(styles['card__action__item'])} key={stub.key}>
      <div
        className={clsx(
          styles['card__action__item__ttl'],
          isCheckinedStub(reservation, product, stub.key) ? styles['used'] : null
        )}
      >
        <p>{getCheckinStubDisplayText(reservation, product, stub)}</p>
        <span>{getCheckinTimeText(reservation, stub.key, i18n.language)}</span>
      </div>
      {notConfirm ? (
        <NotAvailableButton />
      ) : (
        <CheckinButtonForStub reservation={reservation} product={product} stubKey={stub.key} />
      )}
    </li>
  );
};

// Show action buttons for single checkin reservation
const ActionButtons = ({
  reservation,
  product,
  settings,
}: {
  reservation: Reservation;
  product: Product;
  settings: GetSettingsResponse;
}) => {
  const canceled = isReservationCanceled(reservation);
  const notConfirm = isReservationNotConfirmed(reservation);

  const [redeemGuestTypeCounts, setRedeemGuestTypeCounts] = React.useState<GuestTypeCount[]>([]);

  return (
    <div className={clsx(styles['card__acContent'], styles['is-active'])}>
      <ul className={clsx(styles['card__action'])}>
        {config.enableSplitCheckin &&
          product?.qr_checkin_settings?.should_count_guests_for_checkin_with_guest_type && (
            <ETicketGuestCountSelector
              reservation={reservation}
              value={redeemGuestTypeCounts}
              onChange={(newGuestTypeCounts) => {
                setRedeemGuestTypeCounts(newGuestTypeCounts);
              }}
              isBg={false}
            />
          )}

        {reservation?.checkin_info?.checkin_status !== 'CHECKED_IN' && (
          <>
            <li className={clsx(styles['card__action__item'])}>
              {notConfirm ? (
                <NotAvailableButton />
              ) : (
                <CheckinButton
                  reservation={reservation}
                  product={product}
                  guestTypeCounts={redeemGuestTypeCounts}
                />
              )}
            </li>
            {canceled ? (
              <li className={clsx(styles['card__action__item'])}>
                <ReservationCanceledButton />
              </li>
            ) : (
              <>
                {isCancellableSetting(product, settings, reservation) && (
                  <li className={clsx(styles['card__action__item'])}>
                    <CancelButton reservation={reservation} />
                  </li>
                )}
              </>
            )}
          </>
        )}
      </ul>
    </div>
  );
};

const CancelButton = ({ reservation }: { reservation: Reservation }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [inFlight, setInFlight] = React.useState<boolean>(false);

  const reservationId = reservation.id;
  const lastUpdatedDateTimeUtc = reservation.last_updated_date_time_utc;

  const url = useUrl(
    `/reservations/${reservationId}/cancel?ts=${lastUpdatedDateTimeUtc}&b=${router.asPath}`
  );

  return (
    <Link
      href={url}
      className={clsx(styles['btn'], styles['btn--cancel'])}
      onClick={() => {
        setInFlight(true);
      }}
    >
      {inFlight && (
        <Box width="100%" position="absolute">
          <Box width="100%" display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        </Box>
      )}
      {t('Cancel')}
    </Link>
  );
};

// Show Redemption date
const RedemptionDate = ({ reservation }: { reservation: Reservation; product: Product }) => {
  const { t, i18n } = useTranslation();

  return (
    <div className={clsx(styles['card__limit'])}>
      <p className={clsx(styles['card__limit__ttl'])}>{t('Redemption Date Time')}</p>
      <div className={clsx(styles['card__limit__txt'])}>
        {getCheckinTimeText(reservation, '', i18n.language)}
      </div>
    </div>
  );
};

const CheckinButtonForStub = ({
  reservation,
  product,
  stubKey,
}: {
  reservation: Reservation;
  product: Product;
  stubKey: string;
}) => {
  const { t } = useTranslation();

  const { redeemTarget, setRedeemTarget } = React.useContext(ETicketRedeemTargetContext);

  const isDisabled = isCheckinedStub(reservation, product, stubKey) || Boolean(redeemTarget);

  return (
    <>
      <button
        className={clsx(
          styles['btn'],
          styles['width-128'],
          isCheckinedStub(reservation, product, stubKey)
            ? styles['btn--used']
            : styles['btn--submit'],
          isDisabled ? styles['btn--disabled'] : null
        )}
        disabled={isDisabled}
        onClick={() =>
          setRedeemTarget({
            reservation: reservation,
            stubKey: stubKey ?? '',
          })
        }
      >
        {isCheckinedStub(reservation, product, stubKey) ? (
          <>
            <i></i>
            {t('Redeemed')}
          </>
        ) : (
          <>{t('Redeem')}</>
        )}
      </button>
    </>
  );
};

const NotAvailableButton = () => {
  const { t } = useTranslation();

  return (
    <>
      <button
        className={clsx(styles['btn'], styles['width-128'], styles['btn--cancel'])}
        disabled={true}
      >
        <>{t('Not Available')}</>
      </button>
    </>
  );
};

const ReservationCanceledButton = () => {
  const { t } = useTranslation();

  return (
    <>
      <button className={clsx(styles['btn'], styles['btn--cancel'])} disabled={true}>
        <>{t('Canceled')}</>
      </button>
    </>
  );
};

const CheckinButton = ({
  reservation,
  product,
  stubKey,
  guestTypeCounts,
}: {
  reservation: Reservation;
  product: Product;
  stubKey?: string;
  guestTypeCounts?: GuestTypeCount[];
}) => {
  const { t } = useTranslation();

  const { redeemTarget, setRedeemTarget } = React.useContext(ETicketRedeemTargetContext);

  let isDisabled = false;
  if (product?.qr_checkin_settings?.should_count_guests_for_checkin_with_guest_type) {
    isDisabled = guestTypeCounts?.reduce((acc, v) => acc + v.count, 0) === 0;
  } else {
    isDisabled = Boolean(redeemTarget);
  }

  return (
    <>
      <button
        className={clsx(
          styles['btn'],
          styles['btn--redeem'],
          isDisabled ? styles['btn--disabled'] : styles['btn--submit']
        )}
        onClick={() =>
          setRedeemTarget({
            reservation: reservation,
            stubKey: stubKey ?? '',
            guestTypeCounts: guestTypeCounts ?? [],
          })
        }
        disabled={isDisabled}
        style={{
          ...(product?.qr_checkin_settings?.redemption_button_color
            ? { backgroundColor: product?.qr_checkin_settings?.redemption_button_color }
            : {}),
          ...(product?.qr_checkin_settings?.redemption_button_text_color
            ? { color: product?.qr_checkin_settings?.redemption_button_text_color }
            : {}),
        }}
      >
        {product?.qr_checkin_settings?.redemption_button_text
          ? formatTextWithLineBreaks(product?.qr_checkin_settings?.redemption_button_text)
          : t('Redeem')}
      </button>
    </>
  );
};

const getGuestCountByKey = (reservation: Reservation, key: string): number => {
  return reservation.guests.filter((guest) => {
    return guest.guest_type_key === key;
  }).length;
};

const fillZero = (num: number) => {
  return ('00' + num.toString()).slice(-2);
};

const getCheckinStubs = (product: Product) => {
  return product?.qr_checkin_settings?.stubs ?? [];
};
